import type { MvpDashboardSummary } from '@/app/components/vision-dashboard';
import type {
  AqlCohort,
  AqlGrowthCalibration,
  AqlGrowthDashboardPoint,
  AqlGrowthMeasurement,
  AqlOption,
  AqlVisionCapture,
  GrowthFilters
} from '@/app/services/growth-service';
import type { RawVisionAlert, ValidationResult, AnomalyType, Severity } from '@/app/utils/vision-biology';

export const DEMO_AUTH_TOKEN = 'demo_aql_vision_token';
export const DEMO_USER_EMAIL = 'demo@aqlvision.pt';
export const DEMO_ALERT_STORAGE_KEY = 'aql_vision_demo_alert_overrides_v1';

type AqlDesktopBridge = {
  demoAssetUrl?: (fileName: string) => string | null;
  showNotification?: (title: string, options?: { body?: string; silent?: boolean }) => Promise<{ success: boolean; error?: string }>;
};

declare global {
  interface Window {
    aqlDesktop?: AqlDesktopBridge;
  }
}

type DemoAlertOverride = Partial<RawVisionAlert> & {
  status?: RawVisionAlert['status'];
  validation_result?: ValidationResult;
  anomaly_type?: AnomalyType;
  severity?: Severity;
  validation_notes?: string;
  validated_at?: string;
  validatedByEmail?: string;
  hidden?: boolean;
};

const nowMinus = (minutes: number) => new Date(Date.now() - minutes * 60_000).toISOString();

export const getDemoAssetUrl = (fileName: string) => {
  const safeFileName = String(fileName || '')
    .replace(/\\/g, '/')
    .split('/')
    .filter(Boolean)
    .pop();

  if (!safeFileName) return '';

  if (typeof window !== 'undefined') {
    const desktopUrl = window.aqlDesktop?.demoAssetUrl?.(safeFileName);
    if (desktopUrl) return desktopUrl;
  }

  // Use root-relative path so the URL is correct regardless of SPA router depth.
  // base: "./" in vite.config produces relative URLs that break when the router
  // has navigated to a sub-path (e.g. /dashboard → ./demo/ resolves wrongly).
  return `/demo/${encodeURIComponent(safeFileName)}`;
};

export const DEMO_VIDEO_WEBM_FILE = 'aql-vision-pregado-demo.webm';
export const DEMO_VIDEO_MP4_FILE = 'aql-vision-pregado-demo.mp4';
export const DEMO_POSTER_FILE = 'aql-vision-pregado-alert-1.jpg';
export const DEMO_VIDEO_DURATION_SECONDS = 70.83;

export const DEMO_GROWTH_CLIENT_ID = 'demo-aql-pregado';
export const DEMO_GROWTH_PROJECT_ID = 'demo-pregado-pilot';
export const DEMO_GROWTH_LOCATION_ID = 'demo-unidade-piloto';
export const DEMO_GROWTH_ZONE_ID = 'demo-zone-t04';
export const DEMO_GROWTH_SPECIES_ID = 'demo-species-pregado';
export const DEMO_GROWTH_PHASE_ID = 'demo-phase-adult';
export const DEMO_GROWTH_COHORT_ID = 'demo-cohort-pregado-t04-2026';

export const DEMO_DETECTION_TIMELINE = [
  { id: 'demo-visual-lesion-entry', time: 0.25, label: 'zona avermelhada' },
  { id: 'demo-visual-lesion-left-edge', time: 14.35, label: 'lesão periférica' },
  { id: 'demo-env-temperature-peak', time: 30.1, label: 'pico térmico' },
  { id: 'demo-env-oxygen-drop', time: 48.1, label: 'queda O2' },
  { id: 'demo-env-ph-drift', time: 62.1, label: 'deriva pH' },
  { id: 'demo-visual-lesion-late', time: 64.25, label: 'mancha vermelha' },
  { id: 'demo-sensor-calibration', time: 66.5, label: 'sensor por verificar' }
] as const;

export const getDemoDetectionTime = (alertId: string | null | undefined) =>
  DEMO_DETECTION_TIMELINE.find((event) => event.id === alertId)?.time ?? null;

export const getDemoVideoSources = () => ({
  webm: getDemoAssetUrl(DEMO_VIDEO_WEBM_FILE),
  mp4: getDemoAssetUrl(DEMO_VIDEO_MP4_FILE),
  poster: getDemoAssetUrl(DEMO_POSTER_FILE)
});

interface DemoMonitoringEstimate {
  timeSeconds: number;
  cameraCount: number;
  ultrasoundCount: number;
  fusedCount: number;
  confidence: number;
}

export interface DemoGrowthDataset {
  clients: AqlOption[];
  projects: AqlOption[];
  locations: AqlOption[];
  zones: AqlOption[];
  species: AqlOption[];
  phases: AqlOption[];
  cohorts: AqlCohort[];
  series: AqlGrowthDashboardPoint[];
  latestDayMeasurements: AqlGrowthMeasurement[];
  recentMeasurements: AqlGrowthMeasurement[];
  captures: AqlVisionCapture[];
  calibration: AqlGrowthCalibration | null;
}

const round = (value: number, decimals = 1) => Number(value.toFixed(decimals));

const demoClamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const demoNoise = (index: number, seed: number) => {
  const value = Math.sin(index * 12.9898 + seed * 78.233) * 43758.5453123;
  return value - Math.floor(value);
};

const demoDateDaysAgo = (daysAgo: number) => {
  const date = new Date();
  date.setUTCHours(12, 0, 0, 0);
  date.setUTCDate(date.getUTCDate() - daysAgo);
  return date.toISOString().slice(0, 10);
};

const demoIsoMinutesAgo = (minutesAgo: number) => new Date(Date.now() - minutesAgo * 60_000).toISOString();

export const getDemoMonitoringEstimate = (timeSeconds = (Date.now() / 1000) % DEMO_VIDEO_DURATION_SECONDS): DemoMonitoringEstimate => {
  const schooling = Math.sin(timeSeconds / 12.4) * 4 + Math.cos(timeSeconds / 7.8) * 2;
  const micro = Math.sin(timeSeconds / 2.9) * 1.2 + Math.cos(timeSeconds / 1.7) * 0.8;
  const cameraCount = Math.max(14, Math.min(28, Math.round(20 + schooling + micro)));
  const usCycle = Math.sin(timeSeconds / 15.7) * 3.2 + Math.cos(timeSeconds / 8.4) * 1.8;
  const ultrasoundCount = Math.max(13, Math.min(32, Math.round(22 + usCycle)));
  const fusedCount = Math.round(cameraCount * 0.55 + ultrasoundCount * 0.45);
  const agreementPenalty = Math.min(6, Math.abs(cameraCount - ultrasoundCount));
  const confidence = Math.max(0.84, Math.min(0.97, (88 + Math.sin(timeSeconds / 8.3) * 5 - agreementPenalty) / 100));
  return { timeSeconds, cameraCount, ultrasoundCount, fusedCount, confidence };
};

const demoGrowthOptions = () => ({
  clients: [{ id: DEMO_GROWTH_CLIENT_ID, name: 'AQL Demo Aquaculture' }],
  projects: [{ id: DEMO_GROWTH_PROJECT_ID, name: 'Piloto Pregado T-04' }],
  locations: [{ id: DEMO_GROWTH_LOCATION_ID, name: 'Unidade Piloto', code: 'unidade-piloto' }],
  zones: [{ id: DEMO_GROWTH_ZONE_ID, name: 'T-04', code: 'T-04' }],
  species: [{ id: DEMO_GROWTH_SPECIES_ID, name: 'Pregado', code: 'pregado' }],
  phases: [{ id: DEMO_GROWTH_PHASE_ID, name: 'Adulto', code: 'adult' }],
  cohorts: [
    {
      id: DEMO_GROWTH_COHORT_ID,
      name: 'T-04 · Pregado demo 2026',
      species_id: DEMO_GROWTH_SPECIES_ID,
      phase_id: DEMO_GROWTH_PHASE_ID,
      zone_id: DEMO_GROWTH_ZONE_ID,
      project_id: DEMO_GROWTH_PROJECT_ID,
      location_id: DEMO_GROWTH_LOCATION_ID,
      client_id: DEMO_GROWTH_CLIENT_ID,
      start_date: demoDateDaysAgo(112)
    }
  ]
});

const estimateWeight = (lengthMm: number) => round(0.000022 * lengthMm ** 3, 2);

const buildDemoGrowthSeries = (monitoring: DemoMonitoringEstimate): AqlGrowthDashboardPoint[] => {
  const totalDays = 91;
  return Array.from({ length: totalDays }).map((_, index) => {
    const daysAgo = totalDays - index - 1;
    const age = index;
    const liveLift = daysAgo === 0 ? Math.sin(monitoring.timeSeconds / 9) * 0.24 : 0;
    const median = 214.8 + age * 0.36 + Math.sin(age / 8.5) * 1.15 + liveLift;
    const spread = 15.2 - Math.min(5.2, age * 0.045) + Math.sin(age / 12) * 0.7;
    const avg = median + 1.1 + Math.sin(age / 6.4) * 0.55;
    const sampleBase = 330 + monitoring.fusedCount * 5 + Math.sin(age / 5.2) * 32 + demoNoise(index, 4) * 34;
    const sampleCount = Math.round(sampleBase + (daysAgo === 0 ? monitoring.fusedCount * 2 : 0));
    const rejectedCount = Math.max(8, Math.round(sampleCount * (0.035 + demoNoise(index, 8) * 0.025)));
    const confidence = demoClamp(monitoring.confidence + Math.sin(age / 10) * 0.015 + demoNoise(index, 12) * 0.018, 0.86, 0.97);
    const uniformity = demoClamp(0.74 + age * 0.0012 + Math.sin(age / 13) * 0.025 - demoNoise(index, 18) * 0.012, 0.72, 0.88);
    const warningLevel =
      Math.abs(monitoring.cameraCount - monitoring.ultrasoundCount) >= 5 && daysAgo === 0
        ? 'warning'
        : index === 67
          ? 'warning'
          : 'normal';

    return {
      cohort_id: DEMO_GROWTH_COHORT_ID,
      zone_id: DEMO_GROWTH_ZONE_ID,
      period_start: demoDateDaysAgo(daysAgo),
      period_end: null,
      median_size: round(median, 1),
      avg_size: round(avg, 1),
      p10_size: round(median - spread * 1.45, 1),
      p25_size: round(median - spread * 0.55, 1),
      p75_size: round(median + spread * 0.62, 1),
      p90_size: round(median + spread * 1.55, 1),
      sample_count: sampleCount,
      rejected_count: rejectedCount,
      confidence: round(confidence, 3),
      uniformity_score: round(uniformity, 3),
      warning_level: warningLevel,
      has_enough_sample: sampleCount >= 180
    };
  });
};

const buildDemoMeasurements = (latestMedian: number, monitoring: DemoMonitoringEstimate): AqlGrowthMeasurement[] => {
  const totalRows = Math.max(300, Math.min(520, 250 + monitoring.fusedCount * 9));
  return Array.from({ length: totalRows }).map((_, index) => {
    const confidence = demoClamp(0.76 + demoNoise(index, 21) * 0.22 - (index % 47 === 0 ? 0.25 : 0), 0.35, 0.98);
    const isOutlier = index % 83 === 0 || index % 137 === 0;
    const echoOverlap = index % 59 === 0;
    const partialBody = index % 71 === 0;
    const isValid = confidence >= 0.6 && !echoOverlap && !partialBody;
    const distribution = (demoNoise(index, 31) + demoNoise(index, 37) + demoNoise(index, 43) - 1.5) * 14.5;
    const length = latestMedian + distribution + Math.sin(index / 11) * 3.2 + (isOutlier ? 24 + demoNoise(index, 49) * 11 : 0);
    const width = length * (0.47 + demoNoise(index, 53) * 0.055);
    const area = length * width * (0.74 + demoNoise(index, 57) * 0.08);
    const measuredAt = demoIsoMinutesAgo(index * 2.2 + demoNoise(index, 61) * 1.7);
    const captureSlot = Math.floor(index / Math.max(1, Math.round(totalRows / 8)));
    const rejectionReason = isValid
      ? null
      : confidence < 0.6
        ? 'low_confidence'
        : echoOverlap
          ? 'ultrasound_echo_overlap'
          : 'partial_body';

    return {
      id: `demo-growth-measurement-${index + 1}`,
      cohort_id: DEMO_GROWTH_COHORT_ID,
      zone_id: DEMO_GROWTH_ZONE_ID,
      camera_id: 'CAM-04',
      capture_id: `demo-growth-capture-${Math.min(8, captureSlot + 1)}`,
      detection_id: `demo-video-us-detection-${index + 1}`,
      measured_at: measuredAt,
      length_mm: round(length, 1),
      width_mm: round(width, 1),
      area_mm2: round(area, 0),
      estimated_weight_g: estimateWeight(length),
      detection_confidence: round(confidence, 3),
      measurement_quality: round(isValid ? demoClamp(confidence - 0.035 + demoNoise(index, 67) * 0.04, 0.68, 0.98) : confidence * 0.65, 3),
      is_valid: isValid,
      rejection_reason: rejectionReason,
      created_at: measuredAt
    };
  });
};

const buildDemoCaptures = (latestMedian: number, monitoring: DemoMonitoringEstimate): AqlVisionCapture[] => {
  const imageFiles = [
    'aql-vision-pregado-alert-1.jpg',
    'aql-vision-pregado-alert-2.jpg',
    'aql-vision-pregado-alert-3.jpg'
  ];

  return Array.from({ length: 8 }).map((_, index) => ({
    id: `demo-growth-capture-${index + 1}`,
    zone_id: DEMO_GROWTH_ZONE_ID,
    cohort_id: DEMO_GROWTH_COHORT_ID,
    image_url: getDemoAssetUrl(imageFiles[index % imageFiles.length]),
    captured_at: demoIsoMinutesAgo(index * 18 + 2),
    sample_count: Math.round(34 + monitoring.fusedCount + demoNoise(index, 73) * 16),
    median_size: round(latestMedian - index * 0.05 + Math.sin(index) * 0.25, 1)
  }));
};

const buildDemoCalibration = (): AqlGrowthCalibration => ({
  id: 'demo-growth-calibration-t04',
  zone_id: DEMO_GROWTH_ZONE_ID,
  camera_id: 'CAM-04',
  pixel_to_mm_ratio: 0.1925,
  calibration_type: 'video_ultrasound',
  position_code: 'CAM-04 + US-02',
  qr_code: 'AQL-DEMO-T04-CAL-2026',
  quality_score: 0.94,
  valid_from: demoDateDaysAgo(12),
  valid_to: null,
  is_active: true
});

const filterDemoSeriesByRange = (series: AqlGrowthDashboardPoint[], filters?: Partial<GrowthFilters>) => {
  const from = filters?.fromDate ? new Date(`${filters.fromDate}T00:00:00.000Z`).getTime() : Number.NEGATIVE_INFINITY;
  const to = filters?.toDate ? new Date(`${filters.toDate}T23:59:59.999Z`).getTime() : Number.POSITIVE_INFINITY;
  return series.filter((point) => {
    const time = new Date(`${point.period_start.slice(0, 10)}T12:00:00.000Z`).getTime();
    return time >= from && time <= to;
  });
};

export const getDemoGrowthDataset = (filters?: Partial<GrowthFilters>): DemoGrowthDataset => {
  const options = demoGrowthOptions();
  const selectionMatches =
    (!filters?.clientId || filters.clientId === DEMO_GROWTH_CLIENT_ID) &&
    (!filters?.projectId || filters.projectId === DEMO_GROWTH_PROJECT_ID) &&
    (!filters?.locationId || filters.locationId === DEMO_GROWTH_LOCATION_ID) &&
    (!filters?.zoneId || filters.zoneId === DEMO_GROWTH_ZONE_ID) &&
    (!filters?.cohortId || filters.cohortId === DEMO_GROWTH_COHORT_ID) &&
    (!filters?.speciesId || filters.speciesId === DEMO_GROWTH_SPECIES_ID) &&
    (!filters?.phaseId || filters.phaseId === DEMO_GROWTH_PHASE_ID);

  if (!selectionMatches) {
    return {
      ...options,
      series: [],
      latestDayMeasurements: [],
      recentMeasurements: [],
      captures: [],
      calibration: null
    };
  }

  const monitoring = getDemoMonitoringEstimate();
  const series = filterDemoSeriesByRange(buildDemoGrowthSeries(monitoring), filters);
  const latestMedian = (series.length ? series[series.length - 1].median_size : null) ?? 247.4;
  const latestDayMeasurements = buildDemoMeasurements(latestMedian, monitoring);

  return {
    ...options,
    series,
    latestDayMeasurements,
    recentMeasurements: latestDayMeasurements.slice(0, 250),
    captures: buildDemoCaptures(latestMedian, monitoring),
    calibration: buildDemoCalibration()
  };
};

const baseDemoAlerts = (): RawVisionAlert[] => [
  {
    id: 'demo-visual-lesion-entry',
    case_id: 'demo-visual-lesion-entry',
    alert_id: 'demo-alert-001',
    kit_id: 'AQL-DEMO-KIT-04',
    client_id: 'demo-aql-pregado',
    site_id: 'unidade-piloto',
    tank_id: 'T-04',
    userEmail: DEMO_USER_EMAIL,
    imageUrl: getDemoAssetUrl('aql-vision-pregado-alert-1.jpg'),
    species: 'Pregado',
    biological_phase: 'adult',
    detected_at: nowMinus(8),
    model_confidence: 78,
    alert_type: 'Suspeita visual: zona avermelhada',
    severity: 'high',
    status: 'pending',
    problemName: 'Suspeita visual: zona avermelhada',
    problemDescription: 'Área avermelhada detetada na face ocular. Requer validação humana antes de conclusão clínica.',
    proposedSolution: 'Confirmar no tanque, cruzar com comportamento e registar evidência no caso.',
    aiAnalysis: {
      confidence: 0.78,
      detectedIssues: ['zona avermelhada', 'possível lesão superficial'],
      recommendation: 'Validar visualmente e acompanhar evolução nas próximas capturas.'
    },
    createdAt: nowMinus(8),
    metadata: {
      client: 'AQL Demo Aquaculture',
      clientId: 'demo-aql-pregado',
      clientSlug: 'demo-pregado',
      species: 'Pregado',
      phase: 'adult',
      site: 'Unidade Piloto',
      tank: 'T-04',
      location: 'Tanque T-04',
      camera: 'CAM-04',
      kit_id: 'AQL-DEMO-KIT-04',
      temperature: 16.1,
      ph: 7.98,
      oxygen: 7.1,
      detection_status: 'Zona avermelhada · 78%',
      detectionCount: 1
    }
  },
  {
    id: 'demo-visual-lesion-left-edge',
    case_id: 'demo-visual-lesion-left-edge',
    alert_id: 'demo-alert-002',
    kit_id: 'AQL-DEMO-KIT-04',
    client_id: 'demo-aql-pregado',
    site_id: 'unidade-piloto',
    tank_id: 'T-04',
    userEmail: DEMO_USER_EMAIL,
    imageUrl: getDemoAssetUrl('aql-vision-pregado-alert-2.jpg'),
    species: 'Pregado',
    biological_phase: 'adult',
    detected_at: nowMinus(6),
    model_confidence: 72,
    alert_type: 'Suspeita visual: lesão periférica',
    severity: 'medium',
    status: 'pending',
    problemName: 'Suspeita visual: lesão periférica',
    problemDescription: 'Sinal cromático anómalo junto à periferia do peixe. Baixa evidência, pendente de validação.',
    proposedSolution: 'Rever a captura e confirmar se é reflexo, marca física ou lesão real.',
    aiAnalysis: {
      confidence: 0.72,
      detectedIssues: ['lesão periférica suspeita'],
      recommendation: 'Abrir validação humana e comparar com imagens seguintes.'
    },
    createdAt: nowMinus(6),
    metadata: {
      client: 'AQL Demo Aquaculture',
      clientId: 'demo-aql-pregado',
      clientSlug: 'demo-pregado',
      species: 'Pregado',
      phase: 'adult',
      site: 'Unidade Piloto',
      tank: 'T-04',
      location: 'Tanque T-04',
      camera: 'CAM-04',
      kit_id: 'AQL-DEMO-KIT-04',
      temperature: 17.4,
      ph: 8.05,
      oxygen: 6.6,
      detection_status: 'Lesão periférica · 72%',
      detectionCount: 1
    }
  },
  {
    id: 'demo-env-oxygen-drop',
    case_id: 'demo-env-oxygen-drop',
    alert_id: 'demo-alert-003',
    kit_id: 'AQL-DEMO-KIT-04',
    client_id: 'demo-aql-pregado',
    site_id: 'unidade-piloto',
    tank_id: 'T-04',
    userEmail: DEMO_USER_EMAIL,
    imageUrl: getDemoAssetUrl('aql-vision-pregado-alert-3.jpg'),
    species: 'Pregado',
    biological_phase: 'adult',
    detected_at: nowMinus(4),
    model_confidence: 88,
    alert_type: 'O2 dissolvido abaixo do alvo',
    severity: 'critical',
    status: 'pending',
    problemName: 'O2 dissolvido abaixo do alvo',
    problemDescription: 'Leitura simulada de O2 a 3.9 mg/L, abaixo do alvo operacional definido para a demo.',
    proposedSolution: 'Validar sonda, reforçar aeração e acompanhar resposta no tanque.',
    aiAnalysis: {
      confidence: 0.88,
      detectedIssues: ['oxigénio dissolvido baixo', 'risco ambiental'],
      recommendation: 'Priorizar verificação operacional do tanque T-04.'
    },
    createdAt: nowMinus(4),
    metadata: {
      client: 'AQL Demo Aquaculture',
      clientId: 'demo-aql-pregado',
      clientSlug: 'demo-pregado',
      species: 'Pregado',
      phase: 'adult',
      site: 'Unidade Piloto',
      tank: 'T-04',
      location: 'Tanque T-04',
      camera: 'CAM-04',
      kit_id: 'AQL-DEMO-KIT-04',
      temperature: 18.6,
      ph: 8.11,
      oxygen: 3.9,
      detection_status: null,
      detectionCount: 1
    }
  },
  {
    id: 'demo-visual-lesion-late',
    case_id: 'demo-visual-lesion-late',
    alert_id: 'demo-alert-004',
    kit_id: 'AQL-DEMO-KIT-04',
    client_id: 'demo-aql-pregado',
    site_id: 'unidade-piloto',
    tank_id: 'T-04',
    userEmail: DEMO_USER_EMAIL,
    imageUrl: getDemoAssetUrl('aql-vision-pregado-alert-3.jpg'),
    species: 'Pregado',
    biological_phase: 'adult',
    detected_at: nowMinus(2),
    model_confidence: 81,
    alert_type: 'Suspeita visual: mancha vermelha',
    severity: 'high',
    status: 'pending',
    problemName: 'Suspeita visual: mancha vermelha',
    problemDescription: 'Mancha vermelha detetada numa nova passagem de vídeo. O sistema marca como suspeita visual.',
    proposedSolution: 'Abrir validação, registar decisão e comparar com inspeção presencial.',
    aiAnalysis: {
      confidence: 0.81,
      detectedIssues: ['mancha vermelha', 'possível lesão/mancha'],
      recommendation: 'Confirmar se a marca persiste quando o animal muda de posição.'
    },
    createdAt: nowMinus(2),
    metadata: {
      client: 'AQL Demo Aquaculture',
      clientId: 'demo-aql-pregado',
      clientSlug: 'demo-pregado',
      species: 'Pregado',
      phase: 'adult',
      site: 'Unidade Piloto',
      tank: 'T-04',
      location: 'Tanque T-04',
      camera: 'CAM-04',
      kit_id: 'AQL-DEMO-KIT-04',
      temperature: 17.2,
      ph: 8.28,
      oxygen: 5.8,
      detection_status: 'Mancha suspeita · 81%',
      detectionCount: 1
    }
  },
  {
    id: 'demo-env-ph-drift',
    case_id: 'demo-env-ph-drift',
    alert_id: 'demo-alert-006',
    kit_id: 'AQL-DEMO-KIT-04',
    client_id: 'demo-aql-pregado',
    site_id: 'unidade-piloto',
    tank_id: 'T-04',
    userEmail: DEMO_USER_EMAIL,
    imageUrl: getDemoAssetUrl('aql-vision-pregado-alert-3.jpg'),
    species: 'Pregado',
    biological_phase: 'adult',
    detected_at: nowMinus(1.5),
    model_confidence: 79,
    alert_type: 'pH acima do alvo',
    severity: 'medium',
    status: 'pending',
    problemName: 'pH acima do alvo',
    problemDescription: 'Oscilação simulada de pH acima da faixa operacional usada na demo.',
    proposedSolution: 'Confirmar leitura, verificar calibração da sonda e acompanhar estabilidade da água.',
    aiAnalysis: {
      confidence: 0.79,
      detectedIssues: ['pH elevado', 'desvio químico'],
      recommendation: 'Registar contexto operacional e repetir leitura antes de escalar.'
    },
    createdAt: nowMinus(1.5),
    metadata: {
      client: 'AQL Demo Aquaculture',
      clientId: 'demo-aql-pregado',
      clientSlug: 'demo-pregado',
      species: 'Pregado',
      phase: 'adult',
      site: 'Unidade Piloto',
      tank: 'T-04',
      location: 'Tanque T-04',
      camera: 'CAM-04',
      kit_id: 'AQL-DEMO-KIT-04',
      temperature: 17.2,
      ph: 8.34,
      oxygen: 5.9,
      detection_status: null,
      detectionCount: 1
    }
  },
  {
    id: 'demo-env-temperature-peak',
    case_id: 'demo-env-temperature-peak',
    alert_id: 'demo-alert-005',
    kit_id: 'AQL-DEMO-KIT-04',
    client_id: 'demo-aql-pregado',
    site_id: 'unidade-piloto',
    tank_id: 'T-04',
    userEmail: DEMO_USER_EMAIL,
    imageUrl: getDemoAssetUrl('aql-vision-pregado-alert-2.jpg'),
    species: 'Pregado',
    biological_phase: 'adult',
    detected_at: nowMinus(1),
    model_confidence: 84,
    alert_type: 'Temperatura acima do alvo',
    severity: 'medium',
    status: 'pending',
    problemName: 'Temperatura acima do alvo',
    problemDescription: 'Pico simulado de 19.4 °C fora da faixa operacional alvo usada na demo.',
    proposedSolution: 'Confirmar tendência, verificar renovação de água e estabilidade térmica.',
    aiAnalysis: {
      confidence: 0.84,
      detectedIssues: ['pico térmico', 'desvio ambiental'],
      recommendation: 'Monitorizar a tendência antes da próxima janela de alimentação.'
    },
    createdAt: nowMinus(1),
    metadata: {
      client: 'AQL Demo Aquaculture',
      clientId: 'demo-aql-pregado',
      clientSlug: 'demo-pregado',
      species: 'Pregado',
      phase: 'adult',
      site: 'Unidade Piloto',
      tank: 'T-04',
      location: 'Tanque T-04',
      camera: 'CAM-04',
      kit_id: 'AQL-DEMO-KIT-04',
      temperature: 19.4,
      ph: 8.04,
      oxygen: 6.4,
      detection_status: null,
      detectionCount: 1
    }
  },
  {
    id: 'demo-sensor-calibration',
    case_id: 'demo-sensor-calibration',
    alert_id: 'demo-alert-007',
    kit_id: 'AQL-DEMO-KIT-04',
    client_id: 'demo-aql-pregado',
    site_id: 'unidade-piloto',
    tank_id: 'T-04',
    userEmail: DEMO_USER_EMAIL,
    imageUrl: getDemoAssetUrl('aql-vision-pregado-alert-1.jpg'),
    species: 'Pregado',
    biological_phase: 'adult',
    detected_at: nowMinus(0.5),
    model_confidence: 76,
    alert_type: 'Sensor O2 sem calibração',
    severity: 'medium',
    status: 'pending',
    problemName: 'Sensor O2 sem calibração',
    problemDescription: 'O modo demo recebeu uma simulação de falha/calibração pendente na sonda de oxigénio.',
    proposedSolution: 'Confirmar calibração, validar manualmente a leitura e registar intervenção.',
    aiAnalysis: {
      confidence: 0.76,
      detectedIssues: ['sensor por verificar', 'calibração pendente'],
      recommendation: 'Criar nota de validação com decisão operacional.'
    },
    createdAt: nowMinus(0.5),
    metadata: {
      client: 'AQL Demo Aquaculture',
      clientId: 'demo-aql-pregado',
      clientSlug: 'demo-pregado',
      species: 'Pregado',
      phase: 'adult',
      site: 'Unidade Piloto',
      tank: 'T-04',
      location: 'Tanque T-04',
      camera: 'CAM-04',
      kit_id: 'AQL-DEMO-KIT-04',
      temperature: 16.9,
      ph: 8.02,
      oxygen: 6.1,
      detection_status: null,
      detectionCount: 1
    }
  }
];

const readOverrides = (): Record<string, DemoAlertOverride> => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(DEMO_ALERT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const writeOverrides = (overrides: Record<string, DemoAlertOverride>) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(DEMO_ALERT_STORAGE_KEY, JSON.stringify(overrides));
};

export const getDemoVisionAlerts = () => {
  const overrides = readOverrides();
  return baseDemoAlerts()
    .map((alert) => {
      const override = overrides[alert.case_id ?? alert.id] ?? {};
      return {
        ...alert,
        ...override,
        metadata: {
          ...alert.metadata,
          ...(override.metadata ?? {})
        }
      };
    })
    .filter((alert) => !(overrides[alert.case_id ?? alert.id]?.hidden))
    .sort((a, b) => new Date(b.detected_at ?? b.createdAt).getTime() - new Date(a.detected_at ?? a.createdAt).getTime());
};

export const saveDemoAlertValidation = (
  caseId: string,
  patch: Pick<DemoAlertOverride, 'status' | 'validation_result' | 'anomaly_type' | 'severity' | 'validation_notes' | 'validated_at' | 'validatedByEmail'>
) => {
  const overrides = readOverrides();
  overrides[caseId] = {
    ...(overrides[caseId] ?? {}),
    ...patch,
    updatedAt: new Date().toISOString()
  };
  writeOverrides(overrides);
};

export const hideDemoAlert = (caseId: string) => {
  const overrides = readOverrides();
  overrides[caseId] = {
    ...(overrides[caseId] ?? {}),
    hidden: true,
    updatedAt: new Date().toISOString()
  };
  writeOverrides(overrides);
};

export const getDemoDashboardSummary = (alerts: RawVisionAlert[]): MvpDashboardSummary => {
  const pendingValidation = alerts.filter((alert) => String(alert.status ?? '').toLowerCase() === 'pending').length;
  const activeAlerts = alerts.filter((alert) => !['confirmed', 'rejected', 'resolved'].includes(String(alert.status ?? '').toLowerCase())).length;
  const latest = alerts[0] ?? null;
  const latestVisualCapture = alerts.find((alert) => {
    const status = String(alert.metadata?.detection_status ?? '').toLowerCase();
    const type = String(alert.alert_type ?? alert.problemName ?? '').toLowerCase();
    return status.includes('peixe') || type.includes('visual');
  }) ?? latest;
  const latestCaptureAlertId = latestVisualCapture
    ? String(latestVisualCapture.case_id ?? latestVisualCapture.id ?? latestVisualCapture.alert_id ?? '')
    : null;
  const oxygen = Number(latest?.metadata?.oxygen ?? 5.8);
  const temperature = Number(latest?.metadata?.temperature ?? 17.2);
  const ph = Number(latest?.metadata?.ph ?? 8.08);
  const demoVideoSources = getDemoVideoSources();

  return {
    kitId: 'AQL-DEMO-KIT-04',
    status: activeAlerts > 0 ? 'Atenção' : 'OK',
    reason: activeAlerts > 0 ? `${activeAlerts} ${activeAlerts === 1 ? 'alerta em validação' : 'alertas em validação'}` : 'Demo sem alertas pendentes',
    updatedAt: new Date().toISOString(),
    latestCapture: latestVisualCapture ? {
      imageUrl: latestVisualCapture.imageUrl ?? latestVisualCapture.image_url ?? null,
      videoMp4Url: demoVideoSources.mp4,
      videoWebmUrl: demoVideoSources.webm,
      videoStartSeconds: getDemoDetectionTime(latestCaptureAlertId),
      videoLoop: true,
      alertId: latestCaptureAlertId,
      capturedAt: latestVisualCapture.detected_at ?? latestVisualCapture.createdAt,
      tank: 'T-04',
      phase: 'adult',
      species: 'Pregado',
      detectionStatus: latestVisualCapture.metadata?.detection_status ?? null
    } : null,
    alertsActive: activeAlerts,
    pendingValidation,
    sensors: {
      temperature,
      ph,
      oxygen,
      salinity: Number(latest?.metadata?.salinity ?? 30.5),
      status: oxygen < 6 || temperature < 18 || temperature > 22 || ph < 7 || ph > 8.2 ? 'Atenção' : 'OK'
    },
    device: {
      online: true,
      lastCommunication: new Date(Date.now() - 7000).toISOString(),
      batteryPercent: 94,
      communicationAt: new Date(Date.now() - 7000).toISOString(),
      cameraOk: true,
      sensorsOk: true
    }
  };
};
